import React, {Component} from 'react';
import {action, observable} from 'mobx';
import {inject, observer} from 'mobx-react';
import {Button, Row, Col} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import {toast} from 'react-toastify';
import PageTitle from '../../../components/common/PageTitle';
import {saveMyImage} from './myImagesStorage';
import './style.scss';

const IMGLY_SCRIPT_ID = 'imgly-bg-remover-script';
const IMGLY_CDN = 'https://esm.sh/@imgly/background-removal@1.4.5';

// Blob → base64(데이터URL prefix 제거) 변환
const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    const idx = String(result).indexOf(',');
    resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
  };
  reader.onerror = (e) => reject(e);
  reader.readAsDataURL(blob);
});

@inject(({teamUser}) => ({isLogin: teamUser.isLogin, me: teamUser.me}))
@inject(({common}) => ({uploadImages: common.uploadImages, cafe24UploadImages: common.cafe24UploadImages, base64UploadImages: common.base64UploadImages, addMyImage: common.addMyImage}))
@observer
class BgRemover extends Component {

  @observable store = {};
  @observable libReady = false;
  @observable libLoading = true;
  @observable libError = null;

  @observable originalFile = null;
  @observable originalUrl = null;
  @observable resultBlob = null;
  @observable resultUrl = null;
  @observable processing = false;
  @observable progress = 0;
  @observable uploading = false;
  @observable uploadedUrl = null;

  @observable dragOver = false;

  fileInputRef = React.createRef();

  componentDidMount() {
    if (this.props.me) {
      this.store = this.props.me.store ? this.props.me.store : {};
    }
    this.injectImglyScript();
    window.addEventListener('imgly-ready', this.onLibReady);
    window.addEventListener('imgly-error', this.onLibError);
    if (window.__imglyRemoveBackground) this.onLibReady();
  }

  componentWillUnmount() {
    window.removeEventListener('imgly-ready', this.onLibReady);
    window.removeEventListener('imgly-error', this.onLibError);
    if (this.originalUrl) URL.revokeObjectURL(this.originalUrl);
    if (this.resultUrl) URL.revokeObjectURL(this.resultUrl);
  }

  injectImglyScript = () => {
    if (document.getElementById(IMGLY_SCRIPT_ID)) return;
    const script = document.createElement('script');
    script.id = IMGLY_SCRIPT_ID;
    script.type = 'module';
    script.textContent = `
      import { removeBackground } from '${IMGLY_CDN}';
      window.__imglyRemoveBackground = removeBackground;
      window.dispatchEvent(new Event('imgly-ready'));
    `;
    script.onerror = () => window.dispatchEvent(new CustomEvent('imgly-error', {detail: 'script load failed'}));
    document.body.appendChild(script);
  };

  @action onLibReady = () => {
    this.libReady = true;
    this.libLoading = false;
  };

  @action onLibError = (e) => {
    this.libLoading = false;
    this.libError = (e && e.detail) || '라이브러리 로딩 실패';
  };

  @action onFilePicked = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('이미지 파일만 업로드 가능합니다.');
      return;
    }
    if (this.originalUrl) URL.revokeObjectURL(this.originalUrl);
    if (this.resultUrl) URL.revokeObjectURL(this.resultUrl);
    this.originalFile = file;
    this.originalUrl = URL.createObjectURL(file);
    this.resultBlob = null;
    this.resultUrl = null;
    this.uploadedUrl = null;
    this.processing = false;
    this.progress = 0;
  };

  onInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    this.onFilePicked(file);
    e.target.value = '';
  };

  onDragOver = (e) => {
    e.preventDefault();
    this.dragOver = true;
  };
  onDragLeave = (e) => {
    e.preventDefault();
    this.dragOver = false;
  };
  onDrop = (e) => {
    e.preventDefault();
    this.dragOver = false;
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    this.onFilePicked(file);
  };

  @action runBgRemoval = async () => {
    if (!this.libReady) {
      toast.error('배경제거 라이브러리 로딩 중입니다. 잠시 후 다시 시도해 주세요.');
      return;
    }
    if (!this.originalFile) return;
    this.processing = true;
    this.progress = 0;
    try {
      const config = {
        progress: (key, current, total) => {
          if (total > 0) this.progress = Math.round((current / total) * 100);
        },
        output: {format: 'image/png', quality: 0.95}
      };
      const blob = await window.__imglyRemoveBackground(this.originalFile, config);
      if (this.resultUrl) URL.revokeObjectURL(this.resultUrl);
      this.resultBlob = blob;
      this.resultUrl = URL.createObjectURL(blob);
      this.progress = 100;
      toast.success('배경 제거가 완료되었습니다.');
    } catch (err) {
      console.error(err);
      toast.error('배경 제거에 실패했습니다.');
    } finally {
      this.processing = false;
    }
  };

  downloadResult = () => {
    if (!this.resultUrl) return;
    const a = document.createElement('a');
    a.href = this.resultUrl;
    const baseName = (this.originalFile && this.originalFile.name) ? this.originalFile.name.replace(/\.[^.]+$/, '') : 'bg-removed';
    a.download = `${baseName}-bg.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  @action uploadToCafe24 = async () => {
    if (!this.resultBlob) {
      toast.error('먼저 배경 제거를 실행해 주세요.');
      return;
    }
    if (!this.store || !this.store._id) {
      toast.error('스토어 정보를 확인할 수 없습니다.');
      return;
    }
    this.uploading = true;
    try {
      const baseName = (this.originalFile && this.originalFile.name) ? this.originalFile.name.replace(/\.[^.]+$/, '') : 'image';
      const fileName = `${baseName}-bg.png`;

      // 디스크 경유 없이 blob → base64 → 카페24 직접 업로드 (안정성↑)
      const base64 = await blobToBase64(this.resultBlob);
      const resCafe24 = await this.props.base64UploadImages({
        store_id: this.store._id,
        base64Imgs: [{image: base64}]
      });
      if (resCafe24 && resCafe24.error) {
        throw new Error(resCafe24.error.message || '카페24 업로드 실패');
      }
      if (!resCafe24 || !resCafe24.images || resCafe24.images.length === 0) {
        throw new Error('cafe24 upload failed');
      }
      const uploaded = resCafe24.images[0];
      this.uploadedUrl = uploaded.path;

      // 내 이미지 목록에 저장 — 백엔드 우선, 실패 시 localStorage 폴백
      const persist = async (width, height) => {
        const meta = {
          store_id: this.store._id,
          shopNo: this.store.shopNo || 1,
          name: fileName,
          url: uploaded.path,
          size: this.resultBlob.size,
          width: width || 0,
          height: height || 0,
          source: 'bgRemover',
          tags: []
        };
        try {
          let res = await this.props.addMyImage(meta);
          if (!res || !res.data || !res.data._id) throw new Error('backend save failed');
        } catch (e) {
          // 백엔드 실패시 로컬 캐시
          saveMyImage({
            name: fileName,
            url: uploaded.path,
            size: this.resultBlob.size,
            width: width || 0,
            height: height || 0,
            source: 'bgRemover'
          });
        }
      };

      const img = new Image();
      img.onload = () => persist(img.naturalWidth, img.naturalHeight);
      img.onerror = () => persist(0, 0);
      img.src = this.resultUrl;

      toast.success('카페24에 업로드되었습니다. 내 이미지에서 확인하세요.');
    } catch (err) {
      console.error(err);
      toast.error(err && err.message ? err.message : '업로드에 실패했습니다.');
    } finally {
      this.uploading = false;
    }
  };

  copyUrl = () => {
    if (!this.uploadedUrl) return;
    try {
      const tmp = document.createElement('textarea');
      tmp.value = this.uploadedUrl;
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand('copy');
      document.body.removeChild(tmp);
      toast.success('URL이 복사되었습니다.');
    } catch (e) {
      toast.error('URL 복사에 실패했습니다.');
    }
  };

  @action reset = () => {
    if (this.originalUrl) URL.revokeObjectURL(this.originalUrl);
    if (this.resultUrl) URL.revokeObjectURL(this.resultUrl);
    this.originalFile = null;
    this.originalUrl = null;
    this.resultBlob = null;
    this.resultUrl = null;
    this.uploadedUrl = null;
    this.progress = 0;
    this.processing = false;
    this.uploading = false;
  };

  render() {
    return (
      <div className="main-content">
        <PageTitle/>
        <div className="widget-box bg-remover-page">

          <div className="page-head">
            <p>이미지를 업로드하면 브라우저에서 배경을 제거합니다. 결과물을 다운로드하거나 카페24에 업로드할 수 있습니다.</p>
          </div>

          {this.libLoading && (
            <div className="lib-status is-loading">
              <FeatherIcon icon="loader" size="14"/>
              <span>배경제거 엔진 로딩 중… (최초 1회 약 20MB 다운로드)</span>
            </div>
          )}
          {this.libError && (
            <div className="lib-status is-error">
              <FeatherIcon icon="alert-circle" size="14"/>
              <span>{this.libError}</span>
            </div>
          )}

          {!this.originalFile && (
            <div className={`bgr-dropzone ${this.dragOver ? 'is-dragover' : ''}`}
                 onDragOver={this.onDragOver}
                 onDragLeave={this.onDragLeave}
                 onDrop={this.onDrop}
                 onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}>
              <FeatherIcon icon="upload-cloud" size="48"/>
              <div className="bgr-dropzone__title">이미지를 드래그하거나 클릭하여 업로드</div>
              <div className="bgr-dropzone__desc">JPG, PNG, WebP 지원</div>
              <input type="file"
                     ref={this.fileInputRef}
                     accept="image/*"
                     style={{display: 'none'}}
                     onChange={this.onInputChange}/>
            </div>
          )}

          {this.originalFile && (
            <div className="bgr-workspace">
              <Row>
                <Col md="6">
                  <div className="bgr-panel">
                    <div className="bgr-panel__head">
                      <FeatherIcon icon="image" size="16"/>
                      <span>원본</span>
                    </div>
                    <div className="bgr-panel__body">
                      <img src={this.originalUrl} alt="original"/>
                    </div>
                  </div>
                </Col>
                <Col md="6">
                  <div className="bgr-panel">
                    <div className="bgr-panel__head">
                      <FeatherIcon icon="scissors" size="16"/>
                      <span>배경 제거 결과</span>
                    </div>
                    <div className="bgr-panel__body bgr-panel__body--checker">
                      {this.resultUrl
                        ? <img src={this.resultUrl} alt="result"/>
                        : <div className="bgr-empty">
                            {this.processing
                              ? <>
                                  <FeatherIcon icon="loader" size="36"/>
                                  <div>처리 중… {this.progress}%</div>
                                </>
                              : <>
                                  <FeatherIcon icon="zap" size="36"/>
                                  <div>아래 버튼으로 배경 제거를 시작하세요</div>
                                </>
                            }
                          </div>
                      }
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="bgr-actions">
                <Button theme="light" onClick={this.reset}>
                  <FeatherIcon icon="x" size="14"/> 다시 선택
                </Button>
                <Button theme="primary"
                        disabled={!this.libReady || this.processing}
                        onClick={this.runBgRemoval}>
                  <FeatherIcon icon={this.processing ? 'loader' : 'scissors'} size="14"/>
                  {this.processing ? ` 처리 중 ${this.progress}%` : ' 배경 제거'}
                </Button>
                <Button theme="success" disabled={!this.resultBlob} onClick={this.downloadResult}>
                  <FeatherIcon icon="download" size="14"/> 다운로드
                </Button>
                <Button theme="info" disabled={!this.resultBlob || this.uploading} onClick={this.uploadToCafe24}>
                  <FeatherIcon icon={this.uploading ? 'loader' : 'upload'} size="14"/>
                  {this.uploading ? ' 업로드 중…' : ' 카페24 업로드'}
                </Button>
              </div>

              {this.uploadedUrl && (
                <div className="bgr-uploaded">
                  <div className="bgr-uploaded__head">
                    <FeatherIcon icon="check-circle" size="16"/>
                    <span>업로드 완료 — 카페24 URL</span>
                  </div>
                  <div className="bgr-uploaded__row">
                    <input type="text" readOnly value={this.uploadedUrl}/>
                    <Button theme="dark" size="sm" onClick={this.copyUrl}>
                      <FeatherIcon icon="copy" size="14"/> 복사
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    );
  }
}

export default BgRemover;
